// The privacy notice: shown to parents and students before they agree to share, inside the app at
// /privacy, and to anyone at /privacy before signing in.
// When the academy's practice changes, change the wording here and move NOTICE_VERSION on: each consent
// stores the version it was given under.
import { Link } from '../router.js';
import { config } from '../api.js';
import { Icon } from '../ui/icons.js';
import { Brand, Shell } from '../ui/shell.jsx';

export const NOTICE_VERSION = '2026-10c'; // b: what Google sign-in gives the app; c: alerts for missed compulsory tests
const VERSION_LABEL = 'October 2026';

function whatsappNumber() {
  const n = String(config.whatsapp || '').replace(/\D/g, '');
  return n.length === 12 && n.startsWith('91') ? `+91 ${n.slice(2, 7)} ${n.slice(7)}` : `+${n}`;
}

export function NoticeBody({ headingLevel = 2 }) {
  const H = `h${headingLevel}`;
  const wa = `https://wa.me/${config.whatsapp}?text=${encodeURIComponent('Hi Vidyavedh Academy, I have a question about my details in the learning app.')}`;
  return (
    <div class="notice-body">
      <H>Who we are</H>
      <p>Vidyavedh Academy LLP runs this learning app for its ACCA classes. For anything about your details, message us on WhatsApp at <a href={wa} target="_blank" rel="noopener">{whatsappNumber()}</a>.</p>

      <H>What the app keeps</H>
      <p><strong>About a student:</strong> name and academy email; date of birth (so we know whether a parent’s consent is needed); batches and access dates, and membership of each batch’s group in the academy’s Microsoft 365 (which lets the recordings open); classes and recordings opened, and taps on Join for live classes; attendance marked by teachers; answers to tests and mock exams, photos of written answers, and marks; doubts and the photos sent with them; fee payments and receipts; and which devices get notifications (with their browser and device type).</p>
      <p><strong>About a parent or guardian:</strong> name and the email the academy holds for you (you sign in with the Google account for that email); which student you are linked to; the consent you give or withdraw, and when; and which devices get notifications (with their browser and device type).</p>

      <H>Signing in with Google</H>
      <p>When you sign in with Google, Google gives the app your Google account’s basic details: its name, email address, profile photo link and account ID. The app uses them only to sign you in and to find the student the academy linked to that email (and takes your name from them if the academy didn’t have it). Supabase keeps them with your sign-in. The app can’t see your emails, contacts, files or anything else in your Google account, and we don’t sell or pass on what Google gives the app.</p>

      <H>Why</H>
      <p>To run classes, tests and mock exams; to take attendance and follow each student’s progress; to give receipts; to send class and test reminders and, to parents, alerts when a student is marked absent or misses a compulsory test; and to keep a record of consent.</p>

      <H>Who sees it</H>
      <ul>
        <li>The student, the batch’s teachers and the academy’s admins.</li>
        <li>A parent or guardian, only as set out below.</li>
        <li>Services that run the app for us: Supabase (database and sign-in), Netlify (the app’s website), Microsoft (academy accounts, recordings and Teams classes), Google (parents’ sign-in) and Razorpay (online fee payments).</li>
      </ul>
      <p>We don’t sell anyone’s details or use them for advertising.</p>

      <H>Parents and guardians</H>
      <ul>
        <li><strong>Students under 18:</strong> we ask a parent or guardian to give consent, in the app or on a signed form. A parent who has given consent sees the student’s attendance, how many class recordings they have opened, marks in tests and mock exams, topics to work on, coming classes and fee receipts, and can get a notification when the student is marked absent or misses a compulsory test.</li>
        <li><strong>Students of 18 or over</strong> decide which parents, if any, see the same things, in Profile, Sharing with parents.</li>
        <li>Parents don’t see recordings, doubts or test answers.</li>
      </ul>

      <H>Withdrawing consent</H>
      <p>A parent can withdraw consent at any time: open the student in the app and choose Withdraw consent. A student of 18 or over can stop sharing at any time in Profile. Sharing stops straight away; it doesn’t undo what happened before. The academy sees a withdrawal and may contact you about the student’s classes.</p>

      <H>How long we keep it</H>
      <p>We keep a student’s details while they study with us. After that we keep what we need for our records, such as fee receipts, and you can ask us to delete the rest.</p>

      <H>Your choices</H>
      <p>You can ask us what we hold about you or your child, ask us to correct it, or ask us to delete what we don’t need to keep, by messaging us on WhatsApp. If we can’t resolve a complaint, you can take it to the Data Protection Board of India.</p>

      <p class="notice-version">Version {NOTICE_VERSION} ({VERSION_LABEL})</p>
    </div>
  );
}

// Inside the app.
export function PrivacyPage() {
  return (
    <Shell backTo="/profile" backLabel="Profile">
      <p class="eyebrow">Students and parents</p>
      <h1 class="page-title">Privacy notice</h1>
      <p class="muted">How the Vidyavedh learning app uses students’ and parents’ details.</p>
      <article class="card notice-card"><NoticeBody /></article>
    </Shell>
  );
}

// Before signing in.
export function PublicPrivacy() {
  return (
    <div class="signin signin-long">
      <main class="signin-card notice-public" id="content">
        <Brand />
        <h1>Privacy notice</h1>
        <p class="signin-lede">How the Vidyavedh learning app uses students’ and parents’ details.</p>
        <NoticeBody />
        <Link class="btn btn-navy" href="/"><Icon name="prev" /><span>Back to sign in</span></Link>
      </main>
    </div>
  );
}
